import { ButtonHTMLAttributes, FC, ReactElement, useCallback } from 'react';

import { Bets } from '../../config/bets';
import { useHighflyerGameStateSlice } from '../../store/slices/highflyerGame/selectors';
import { useHighflyerStateSlice } from '../../store/slices/highflyerState/selectors';
import { useButtonStatusConnected } from '../gameGrid/hooks/useButtonStatusConnected';
import { useGameBetDisplayAmountConnected } from '../gameGrid/hooks/useGameBetDisplayAmountConnected';
import { useIsConfirmButtonDisabled } from '../gameGrid/hooks/useIsConfirmButtonDisabled';
import { useOnBetCallbackConnected } from '../gameGrid/hooks/useOnBetCallbackConnected';
import { useOnCancelCallbackConnected } from '../gameGrid/hooks/useOnCancelCallbackConnected';
import { useOnCashoutCallBackConnected } from '../gameGrid/hooks/useOnCashoutCallBackConnected';
import { useValidBetInputValue } from '../gameGrid/hooks/useValidBetInputValue';

import { FloatingGameBetButton } from './FloatingGameBetButton';
import { GameBetButton } from './GameBetButton';
import { BUTTON_STATES } from './types';

export interface GameBetButtonConnectedProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  betCode: Bets;
  isFloatingButton?: boolean;
}

export const GameBetButtonConnected: FC<GameBetButtonConnectedProps> = ({
  betCode,
  isFloatingButton = false,
}): ReactElement => {
  const onBet = useOnBetCallbackConnected();
  const onCancel = useOnCancelCallbackConnected(betCode);
  const onCashOut = useOnCashoutCallBackConnected();
  const { betInputAmount } = useValidBetInputValue(betCode);
  const displayAmount = useGameBetDisplayAmountConnected(betCode);
  const { label: Button_Label, state: Button_State } = useButtonStatusConnected(betCode);
  const isDisabled = useIsConfirmButtonDisabled(betCode);
  const isLocalCashed = useHighflyerStateSlice.isLocalCashed(betCode);
  const isSecondBetBoardEnabled = useHighflyerGameStateSlice.getIsSecondBetBoardToggle();

  const handleBetClick = useCallback(
    (actionType: BUTTON_STATES) => {
      switch (actionType) {
        case BUTTON_STATES.confirm:
          onBet(betCode, betInputAmount);
          break;
        case BUTTON_STATES.cancel:
          onCancel(betCode);
          break;
        case BUTTON_STATES.cashout:
          onCashOut(betCode);
          break;
      }
    },
    [betInputAmount, onBet, onCancel, onCashOut, betCode]
  );

  return (
    <>
      {isFloatingButton ? (
        <FloatingGameBetButton
          buttonLabel={Button_Label}
          buttonState={Button_State}
          displayAmount={displayAmount}
          isDisabled={isDisabled}
          handleBetClick={handleBetClick}
          isLocalCashed={isLocalCashed}
        />
      ) : (
        <GameBetButton
          buttonLabel={Button_Label}
          buttonState={Button_State}
          displayAmount={displayAmount}
          isDisabled={isDisabled && Button_State !== BUTTON_STATES.lowbalance}
          handleBetClick={handleBetClick}
          isLocalCashed={isLocalCashed}
          isSecondBetBoardEnabled={isSecondBetBoardEnabled}
        />
      )}
    </>
  );
};
